/**
 * The guitar side of a completed chord: the same notes, shown the way a guitarist
 * reads them. Standard notation of the exact voicing, six-line TAB, a chord box,
 * and a browser for the other playable shapes.
 *
 * Listen plays precisely what is displayed — not the abstract chord — so the
 * omitted 5th or doubled root you can see is the one you hear.
 */

import { useEffect, useMemo, useState } from 'react';
import { Score } from './Score';
import { GuitarTab } from './GuitarTab';
import { FretDiagram } from './FretDiagram';
import {
  STRING_NAMES,
  findVoicings,
  fretString,
  writtenForGuitar,
  type GuitarVoicing,
} from '../theory/guitar';
import type { ChordQuality } from '../theory/chords';
import { pitchName, type Pitch } from '../theory/pitch';
import { playVoicing, stop } from '../audio/player';
import { Button } from '../design/Button';

export interface GuitarViewProps {
  chord: Pitch[];
  quality: ChordQuality;
  chordName: string;
}

export function GuitarView({ chord, quality, chordName }: GuitarViewProps) {
  const voicings = useMemo(() => findVoicings(chord, quality, 8), [chord, quality]);
  const [index, setIndex] = useState(0);

  // A new chord resets the browser to the best-ranked shape.
  useEffect(() => {
    setIndex(0);
  }, [voicings]);

  if (!voicings.length) {
    return (
      <div className="guitar-view">
        <p className="caveat">
          No playable shape for {chordName} fits under six strings, a four-fret span, and four
          fingers between the nut and the 12th fret. That is a real answer, not a failure —
          some dense voicings genuinely need a different instrument or a wider register.
        </p>
      </div>
    );
  }

  const voicing: GuitarVoicing = voicings[Math.min(index, voicings.length - 1)];

  return (
    <div className="guitar-view">
      <div className="guitar-head">
        <div>
          <p className="eyebrow">Guitar · standard tuning E A D G B e</p>
          <p className="chord-name">{chordName}</p>
          <p className="chord-sub">
            Shape {index + 1} of {voicings.length} · <code>{fretString(voicing)}</code>
            {voicing.barre !== null ? ` · barre at fret ${voicing.barre}` : ''} ·{' '}
            {voicing.fingers} finger{voicing.fingers === 1 ? '' : 's'}
          </p>
        </div>

        <div className="play-row">
          {/* Strummed low string to high, because a guitar chord is not six
              simultaneous notes — and the sounding pitches, not the written ones. */}
          <Button variant="primary" icon="play" onClick={() => void playVoicing(voicing.pitches)}>
            Listen
          </Button>
          <Button variant="tertiary" icon="stop" onClick={stop}>
            Stop
          </Button>
        </div>
      </div>

      <div className="guitar-grid">
        <div className="guitar-diagram">
          <FretDiagram voicing={voicing} />
          <div className="voicing-nav">
            <Button
              variant="secondary"
              icon="arrowLeft"
              disabled={index === 0}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              icon="arrowRight"
              iconPosition="end"
              disabled={index >= voicings.length - 1}
              onClick={() => setIndex((i) => Math.min(voicings.length - 1, i + 1))}
            >
              Next
            </Button>
          </div>
        </div>

        <div className="guitar-notation">
          {/*
            Written an octave above what the strings sound, with the 8vb clef
            saying so. TAB, the readout below and Listen all use the sounding
            pitches — the transform belongs to this staff and nothing else.
          */}
          <Score
            clef="treble"
            octaveShift="8vb"
            groups={[voicing.pitches.map(writtenForGuitar)]}
            height={170}
          />
          <p className="guitar-octave-note">
            Guitar notation sounds an octave lower than it is written — the small 8
            under the clef says so. The tab, the string list and Listen are the pitches
            the strings actually make.
          </p>
          <GuitarTab voicing={voicing} />
        </div>
      </div>

      <ul className="string-readout">
        {voicing.frets.map((fret, i) => (
          <li key={i} className={fret === null ? 'is-muted' : ''}>
            <span className="string-name">{STRING_NAMES[i]}</span>
            <span className="string-fret">{fret === null ? '×' : fret === 0 ? 'open' : fret}</span>
            <span className="string-pitch">
              {pitchAt(voicing, i) ? pitchName(pitchAt(voicing, i)!) : 'muted'}
            </span>
          </li>
        ))}
      </ul>

      {voicing.omittedDegrees.length ? (
        <p className="caveat">
          This shape leaves out the {voicing.omittedDegrees.join(' and ')}. Six strings and four
          fingers cannot always hold every tone — guitarists drop the 5th first because it adds
          the least to the chord's identity.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The sounding pitch on a given string, or null when it is muted.
 *
 * This used to count how many strings below were sounding and index into
 * `pitches`, which assumes string order and pitch order are the same thing. They
 * are not: fretting the low E above an open A string reverses them, and the
 * readout then named each note one string out — the diagram, the tab and the
 * staff all agreed with each other and disagreed with this list.
 */
function pitchAt(voicing: GuitarVoicing, stringIndex: number): Pitch | null {
  return voicing.notesByString[stringIndex] ?? null;
}
